import { Input } from '@/shared/components/ui/Input';
import type { TemplateRow } from '@/features/configuracoes/api';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import type { MessageEventVariable, MessageEventVariables } from './api';

/**
 * Componente Meta de template — formato armazenado em `templates.components`.
 * Campos relevantes pra extrair variáveis:
 *   - BODY.text com {{N}}
 *   - HEADER.format = TEXT (text com {{N}})
 *   - HEADER.format = IMAGE/VIDEO/DOCUMENT (sem text, mas precisa de link)
 *   - BUTTONS.buttons[i].url com {{N}}
 */
interface MetaTemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  buttons?: Array<{ type: string; text?: string; url?: string }>;
}

/**
 * Extrai a lista de "slots" de variáveis configuráveis de um template.
 * Retorna chaves canônicas usadas em `template_variables`.
 */
interface VariableSlot {
  key: string;
  label: string;
  /** Se true, suporta `kind: 'upload'` (só header.media). */
  mediaKind: boolean;
  /** Header de DOCUMENTO → mostra campo "Nome do arquivo" (filename do WhatsApp). */
  isDocument?: boolean;
}

function extractSlots(components: unknown[]): VariableSlot[] {
  const slots: VariableSlot[] = [];
  for (const raw of components as MetaTemplateComponent[]) {
    if (!raw?.type) continue;
    if (raw.type === 'BODY' && raw.text) {
      const nums = extractVarNumbers(raw.text);
      for (const n of nums) {
        slots.push({ key: `body.${n}`, label: `Body {{${n}}}`, mediaKind: false });
      }
    } else if (raw.type === 'HEADER') {
      if (raw.format === 'TEXT' && raw.text) {
        const nums = extractVarNumbers(raw.text);
        for (const n of nums) {
          slots.push({ key: `header.text.${n}`, label: `Header {{${n}}}`, mediaKind: false });
        }
      } else if (
        raw.format === 'IMAGE' ||
        raw.format === 'VIDEO' ||
        raw.format === 'DOCUMENT'
      ) {
        slots.push({
          key: 'header.media',
          label: `Header (${raw.format.toLowerCase()})`,
          mediaKind: true,
          isDocument: raw.format === 'DOCUMENT',
        });
      }
    } else if (raw.type === 'BUTTONS' && raw.buttons) {
      raw.buttons.forEach((btn, i) => {
        if (btn?.type === 'URL' && btn.url && /\{\{\s*\d+\s*\}\}/.test(btn.url)) {
          slots.push({
            key: `button.${i}`,
            label: `Botão URL "${btn.text ?? `#${i}`}" — sufixo da URL`,
            mediaKind: false,
          });
        }
      });
    }
  }
  return slots;
}

function extractVarNumbers(text: string): number[] {
  const matches = text.match(/\{\{\s*(\d+)\s*\}\}/g) ?? [];
  const nums = matches.map((m) => Number(m.replace(/\D/g, '')));
  return [...new Set(nums)].sort((a, b) => a - b);
}

function templateBodyText(template: TemplateRow): string {
  const body = (template.components as MetaTemplateComponent[]).find((c) => c.type === 'BODY');
  return body?.text ?? '';
}

interface Props {
  templates: TemplateRow[];
  isLoading: boolean;
  selectedTemplateId: number | null;
  onSelectTemplate: (id: number | null) => void;
  variables: MessageEventVariables;
  onVariablesChange: (next: MessageEventVariables) => void;
  jsonPaths: string[];
  /** Indica se o usuário anexou arquivo (campo "Arquivo (opcional)" do editor)
   *  — habilita opção `kind=upload` no header.media. */
  hasLeadMediaPath: boolean;
}

export function TemplateBuilder({
  templates,
  isLoading,
  selectedTemplateId,
  onSelectTemplate,
  variables,
  onVariablesChange,
  jsonPaths,
  hasLeadMediaPath,
}: Props) {
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const slots = useMemo(
    () => (selectedTemplate ? extractSlots(selectedTemplate.components) : []),
    [selectedTemplate],
  );

  const setVar = (key: string, v: MessageEventVariable) => {
    onVariablesChange({ ...variables, [key]: v });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-[12px] text-n-600">
          Template HSM <span className="text-danger-500">*</span>
        </label>
        {isLoading ? (
          <div className="flex h-9 items-center gap-2 text-[12px] text-n-500">
            <Loader2 size={14} className="animate-spin" />
            Carregando templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
            Nenhum template aprovado encontrado pra este canal. Sincronize templates em
            Configurações → Templates ou aguarde aprovação da Meta.
          </div>
        ) : (
          <select
            value={selectedTemplateId ?? ''}
            onChange={(e) =>
              onSelectTemplate(e.target.value ? Number(e.target.value) : null)
            }
            className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm text-n-900 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
          >
            <option value="">Selecione um template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.language}) — {t.category}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedTemplate && (
        <>
          {/* Preview do body */}
          <div className="rounded-md border border-n-200 bg-n-50 p-3 text-[12px] text-n-700">
            <div className="mb-1 font-semibold text-n-500">Preview do body</div>
            <div className="whitespace-pre-wrap font-mono">
              {templateBodyText(selectedTemplate) || '(sem body)'}
            </div>
          </div>

          {/* Variáveis */}
          {slots.length > 0 ? (
            <div className="space-y-2">
              <div className="text-[12px] font-semibold text-n-700">
                Preencher variáveis ({slots.length})
              </div>
              {slots.map((slot) => (
                <VariableRow
                  key={slot.key}
                  slot={slot}
                  value={variables[slot.key]}
                  onChange={(v) => setVar(slot.key, v)}
                  jsonPaths={jsonPaths}
                  hasLeadMediaPath={hasLeadMediaPath}
                />
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-n-500">
              Este template não tem variáveis configuráveis — basta selecionar.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function VariableRow({
  slot,
  value,
  onChange,
  jsonPaths,
  hasLeadMediaPath,
}: {
  slot: VariableSlot;
  value: MessageEventVariable | undefined;
  onChange: (v: MessageEventVariable) => void;
  jsonPaths: string[];
  hasLeadMediaPath: boolean;
}) {
  const kind = value?.kind ?? 'fixed';
  // Preserva o filename (header de documento) ao trocar de kind/valor.
  const keepFn: { filename?: string } = value?.filename ? { filename: value.filename } : {};

  return (
    <div className="rounded-md border border-n-200 bg-n-0 p-3">
      <div className="mb-2 text-[12px] font-semibold text-n-700">{slot.label}</div>
      <div className="mb-2 flex flex-wrap gap-3">
        <KindRadio
          name={`kind-${slot.key}`}
          checked={kind === 'fixed'}
          onChange={() =>
            onChange({ kind: 'fixed', value: value?.kind === 'fixed' ? value.value : '', ...keepFn })
          }
          label="Texto fixo"
        />
        <KindRadio
          name={`kind-${slot.key}`}
          checked={kind === 'path'}
          onChange={() =>
            onChange({ kind: 'path', value: value?.kind === 'path' ? value.value : '', ...keepFn })
          }
          label="Variável do payload"
        />
        {slot.mediaKind && (
          <KindRadio
            name={`kind-${slot.key}`}
            checked={kind === 'upload'}
            onChange={() => onChange({ kind: 'upload', ...keepFn })}
            label="Usar arquivo do lead"
            disabled={!hasLeadMediaPath}
            {...(!hasLeadMediaPath && {
              tooltip: 'Configure "Campo do payload com a URL do arquivo" em Dados do Lead',
            })}
          />
        )}
      </div>

      {kind === 'fixed' && (
        <Input
          value={value?.kind === 'fixed' ? value.value : ''}
          onChange={(e) => onChange({ kind: 'fixed', value: e.target.value, ...keepFn })}
          placeholder={slot.mediaKind ? 'https://exemplo.com/arquivo.png' : 'Valor fixo…'}
        />
      )}
      {kind === 'path' && (
        <select
          value={value?.kind === 'path' ? value.value : ''}
          onChange={(e) => onChange({ kind: 'path', value: e.target.value, ...keepFn })}
          className="h-9 w-full rounded-md border border-n-300 bg-n-0 px-3 text-sm text-n-900 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
        >
          <option value="">Selecione um campo do payload…</option>
          {jsonPaths.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      )}
      {kind === 'upload' && (
        <p className="text-[12px] text-n-500">
          Usará a URL configurada em <strong>Dados do Lead → arquivo</strong>.
        </p>
      )}

      {slot.isDocument && (
        <div className="mt-3 border-t border-n-100 pt-3">
          <label className="mb-1 block text-[12px] font-medium text-n-700">
            Nome do arquivo (opcional)
          </label>
          <Input
            value={value?.filename ?? ''}
            onChange={(e) => {
              const fn = e.target.value;
              const base: MessageEventVariable = value ?? { kind: 'fixed', value: '' };
              if (fn) {
                onChange({ ...base, filename: fn } as MessageEventVariable);
              } else if (base.kind === 'upload') {
                onChange({ kind: 'upload' });
              } else {
                onChange({ kind: base.kind, value: base.value });
              }
            }}
            placeholder="Ex: Boleto.pdf"
          />
          <p className="mt-1 text-[11px] text-n-500">
            Nome que o destinatário vê no WhatsApp. Sem isso, aparece “Sem Título”.
          </p>
        </div>
      )}
    </div>
  );
}

function KindRadio({
  name,
  checked,
  onChange,
  label,
  disabled,
  tooltip,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
  tooltip?: string;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 text-[12px] ${
        disabled ? 'cursor-not-allowed text-n-400' : 'cursor-pointer text-n-700'
      }`}
      title={tooltip}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      {label}
    </label>
  );
}
